import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { usePerson } from '@/account/usePerson';
import { ChoiceCards } from '@/components/ChoiceCards/ChoiceCards';
import { JobGlyph } from '@/components/JobGlyph/JobGlyph';
import { NewJobForm } from '@/components/NewJobForm/NewJobForm';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import {
  allJobs,
  awaitScreening,
  DELETE_JOB,
  describeSubmitError,
  isAudioSample,
  isInFlight,
  JOBS,
  jobTitle,
  listSamples,
  rejectionTitle,
  submitJob,
  type JobKind,
  type JobOptions,
  type JobSource,
  type Sample,
} from '@/papermill/api';
import { DemoKeyError, withDemoKey } from '@/papermill/demo-key';

type Mode = 'batch' | 'live';

const KINDS = [
  {
    value: 'document' as const,
    title: 'Document',
    description:
      'A PDF, a photo or an Office file. Papermill cleans it up, reads every page and links what it finds.',
    icon: <JobGlyph source="document" />,
  },
  {
    value: 'audio' as const,
    title: 'Audio',
    description:
      'A call or a meeting. Stylus transcribes who said what, then Papermill reads the conversation.',
    icon: <JobGlyph source="audio" />,
  },
];

const MODES = [
  {
    value: 'batch' as const,
    title: 'Upload a recording',
    description: 'An audio or video file, up to 500 MB. One credit per minute; samples are free.',
    icon: '⇪',
  },
  {
    value: 'live' as const,
    title: 'Live',
    description: 'Talk, and watch every word land on screen as you say it. Two credits a minute.',
    icon: <JobGlyph source="live" active />,
  },
];

/** Start a job, on the Demo key: choose a document or a recording, then a file or a sample. */
export function NewJobContainer() {
  const client = useApolloClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const kindParam = params.get('kind');
  const kind: JobKind | null = kindParam === 'audio' || kindParam === 'document' ? kindParam : null;
  const mode: Mode | null = kind === 'audio' && params.get('mode') === 'batch' ? 'batch' : null;
  const { person } = usePerson();
  const { data } = useQuery(JOBS, { fetchPolicy: 'cache-and-network' });
  const running = useMemo(() => allJobs(data).find(isInFlight) ?? null, [data]);
  const [progress, setProgress] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejection, setRejection] = useState<{ title: string; message: string } | null>(null);
  const [deleteJob] = useMutation(DELETE_JOB);
  const [samples, setSamples] = useState<Sample[] | null>(null);
  const [samplesLoading, setSamplesLoading] = useState(false);

  const explain = (failure: unknown) =>
    failure instanceof DemoKeyError ? failure.message : describeSubmitError(failure);

  const submit = useCallback(
    async (source: JobSource, options: JobOptions = {}, screen = true) => {
      if (!person) {
        return;
      }
      setError(null);
      setRejection(null);
      setProgress(0);
      try {
        const id = await withDemoKey(client, person.id, (secret) =>
          submitJob(source, secret, setProgress, options),
        );
        setProgress(null);
        setChecking(screen);
        const screening = screen ? await awaitScreening(client, id) : null;
        if (screening?.outcome === 'rejected') {
          await deleteJob({ variables: { id } }).catch(() => undefined);
          setRejection({
            title: rejectionTitle(screening.error.code),
            message: screening.error.message,
          });
          setChecking(false);
          return;
        }
        await client.refetchQueries({ include: [JOBS] });
        navigate('/playground', { state: { highlight: id } });
      } catch (failure) {
        setError(explain(failure));
        setProgress(null);
        setChecking(false);
      }
    },
    [client, deleteJob, navigate, person],
  );

  const showSamples = useCallback(async () => {
    if (!person) {
      return;
    }
    setSamplesLoading(true);
    setError(null);
    try {
      setSamples(await withDemoKey(client, person.id, listSamples));
    } catch (failure) {
      setError(explain(failure));
    } finally {
      setSamplesLoading(false);
    }
  }, [client, person]);

  const choose = (next: Record<string, string>) => {
    setError(null);
    setRejection(null);
    setParams(next);
  };
  const kindSamples =
    samples && kind
      ? samples.filter((sample) => isAudioSample(sample) === (kind === 'audio'))
      : samples;
  const audioForm = kind === 'audio' && mode === 'batch';
  const back =
    kind === 'audio' && mode ? (
      <Link to="?kind=audio">← Audio</Link>
    ) : kind ? (
      <Link to="/playground/new">← Choose what to run</Link>
    ) : (
      <Link to="/playground">← All jobs</Link>
    );

  if (!kind || (kind === 'audio' && !mode)) {
    return (
      <>
        <PageHeader
          back={back}
          title={kind === 'audio' ? 'New recording' : 'New job'}
          lede={
            kind === 'audio'
              ? 'Upload a recording, or talk live and watch the words appear as you say them.'
              : 'What would you like to run? Every job ends in a graph you can explore, with each value traced to where it came from.'
          }
        />
        {kind === 'audio' ? (
          <ChoiceCards
            label="How to capture it"
            choices={MODES}
            onChoose={(value) =>
              value === 'live'
                ? navigate('/playground/live')
                : choose({ kind: 'audio', mode: value })
            }
          />
        ) : (
          <ChoiceCards
            label="What to run"
            choices={KINDS}
            onChoose={(value) => choose({ kind: value })}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        back={back}
        title={audioForm ? 'Upload a recording' : 'New document'}
        lede={
          audioForm
            ? 'A call, a meeting or a voice note. Stylus writes down who said what, then Papermill finds the people, employers, accounts and addresses in it.'
            : 'Upload a document or take a photo of one. Papermill cleans it up, reads every page, and links what it finds.'
        }
      />
      <NewJobForm
        kind={kind}
        onSubmitFile={(file, options) => void submit({ file }, options, !audioForm)}
        onSubmitSample={(sample) => void submit({ sample }, {}, !audioForm)}
        progress={progress}
        checking={checking}
        error={error}
        rejection={rejection}
        blocked={
          running
            ? `“${jobTitle(running)}” is still being processed. The playground takes one job at a time on your account.`
            : null
        }
        samples={kindSamples}
        samplesLoading={samplesLoading}
        onShowSamples={() => void showSamples()}
      />
    </>
  );
}
